import BackButton from "./BackButton";
import { ReactNode } from "react";
import { Search } from "@mui/icons-material";
import {
  Box,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";

interface ModernTableProps<T> {
  data: T[];
  columns: TableColumn<T>[];
  searchPlaceholder?: string;
  onSearch?: (term: string) => void;
  searchValue?: string;
  emptyMessage?: string;
  showBackButton?: boolean;
  onBack?: () => void;
  backButtonLabel?: string;
  renderRow?: (item: T, index: number) => ReactNode;
  onRowClick?: (item: T, index: number) => void;
  getItemKey?: (item: T, index: number) => string | number;
  headerActions?: ReactNode;
  secondaryHeaderActions?: ReactNode;
}

export interface TableColumn<T> {
  id: string;
  label: string;
  align?: "left" | "center" | "right";
  width?: string | number;
  render?: (value: T[keyof T], item: T, index: number) => ReactNode;
}

export default function ModernTable<T>({
  data,
  columns,
  searchPlaceholder = "Wyszukaj...",
  onSearch,
  searchValue = "",
  emptyMessage = "Brak danych",
  showBackButton = false,
  onBack,
  backButtonLabel = "Wstecz",
  renderRow,
  onRowClick,
  getItemKey,
  headerActions,
  secondaryHeaderActions,
}: ModernTableProps<T>) {
  const isRowClickable = !!onRowClick;

  const getKey = (item: T, index: number): string | number => {
    if (getItemKey) {
      return getItemKey(item, index);
    }
    // Fallback: try to get id property if exists
    if (typeof item === "object" && item !== null && "id" in item) {
      return (item as { id: string | number }).id;
    }
    return index;
  };
  return (
    <TableContainer component={Paper}>
      <Box sx={{ p: { xs: 1.5, sm: 2 }, borderBottom: 1, borderColor: "divider" }}>
        <Box
          sx={{
            display: "flex",
            gap: { xs: 0.75, sm: 2 },
            alignItems: "center",
            flexWrap: { xs: "nowrap", sm: "wrap" },
          }}
        >
          {onSearch && (
            <TextField
              size="small"
              fullWidth
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={(e) => onSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{
                maxWidth: { xs: "none", sm: 400 },
                flexGrow: 1,
                minWidth: 0,
                "& .MuiOutlinedInput-root": {
                  paddingLeft: { xs: "8px", sm: "14px" },
                },
              }}
            />
          )}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 0.5, sm: 1 },
              flexShrink: 0,
              ml: "auto",
              justifyContent: "flex-end",
            }}
          >
            {headerActions && (
              <Box sx={{ display: "flex", alignItems: "center" }}>{headerActions}</Box>
            )}
            {showBackButton && onBack && (
              <BackButton onClick={onBack} label={backButtonLabel || "Wróć"} />
            )}
          </Box>
        </Box>
        {secondaryHeaderActions && (
          <Box
            sx={{
              mt: 1,
              ml:1,
              display: "flex",
              width: "100%",
              justifyContent: "flex-start",
            }}
          >
            {secondaryHeaderActions}
          </Box>
        )}
      </Box>
      <Table sx={{ borderCollapse: "separate", borderSpacing: 0 }}>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column.id}
                align={column.align || "left"}
                sx={{ fontWeight: 600, fontSize: "0.875rem", width: column.width }}
              >
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                {emptyMessage}
              </TableCell>
            </TableRow>
          ) : (
            data.map((item, index) => {
              const key = getKey(item, index);
              return renderRow ? (
                <TableRow
                  key={key}
                  hover={isRowClickable}
                  onClick={() => onRowClick?.(item, index)}
                  sx={{
                    cursor: isRowClickable ? "pointer" : "default",
                  }}
                >
                  {renderRow(item, index)}
                </TableRow>
              ) : (
                <TableRow
                  key={key}
                  hover={isRowClickable}
                  onClick={() => onRowClick?.(item, index)}
                  sx={{
                    cursor: isRowClickable ? "pointer" : "default",
                  }}
                >
                  {columns.map((column) => (
                    <TableCell key={column.id} align={column.align || "left"}>
                      {column.render
                        ? column.render(
                            (item as Record<string, unknown>)[column.id] as T[keyof T],
                            item,
                            index,
                          )
                        : String((item as Record<string, unknown>)[column.id] ?? "")}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
